import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Table,
  Box,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  TableContainer,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  TableHead,
  Stack,
} from "@mui/material";
import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import UnitsRadioGroup from "../../../../Inputs/HelpDisplay/UnitsRadioGroup";
import {
  getMassUnits,
  MassToCompensate,
  ValueOrNull,
} from "../../../../../utils/helpers/tolerance/GeneralHelper";
import { useUnits } from "../../../../../hooks/useMeasureInUnits";
import { toFixed } from "../../../../../utils/helpers/NumberHelper";
import HelpInput from "../../../../Inputs/HelpInput/HelpInput";
import { HelpOutput } from "../../../../Inputs/HelpDisplay/HelpOutput";

const DEFAULT_MASS = 1000;

export default function PermissibleUnbalanceMassCalculation({
  supports = 2,
  residualDisbalanceByPlane = 0,
}) {
  const { t } = useTranslation();
  const [radii, setRadii] = useState(Array(5).fill(0));
  const allowedMassUnits = useMemo(() => getMassUnits(t).slice(0, 3), [t]);

  const { unitLabel, unit, handleUnitChange } = useUnits(
    null,
    allowedMassUnits[1]
  );

  const handleChangeByIndex = useCallback((index, value) => {
    if (value < 0) return;
    setRadii((prevValues) => {
      const newValues = [...prevValues];
      newValues[index] = value;
      return newValues;
    });
  }, []);

  const disbalanceMassValue = useMemo(() => {
    return radii.slice(0, supports).map((radius) => {
      const mass = MassToCompensate(residualDisbalanceByPlane, radius);
      const value = (mass * unit.value) / DEFAULT_MASS;
      const roundedValue = toFixed(value);
      return ValueOrNull(roundedValue);
    });
  }, [radii, supports, residualDisbalanceByPlane, unit]);

  return (
    <Accordion sx={{ mt: 2 }}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography variant="h5">
          {t(
            "shared.disbalance.components.permissibleUnbalancedMassCalculation.title"
          )}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: { sm: "block", md: "none" } }}>
          <Typography variant="h6" sx={{ my: 2 }}>
            {t(
              "pages.driveshaftTolerance.components.other.permissibleUnbalanceMassCalculation.mobile.components.inputs.title"
            )}
            , {t("units.length.mm")}
          </Typography>
          <Stack sx={{ gap: 2 }}>
            {radii.slice(0, supports).map((value, index) => (
              <HelpInput
                label={
                  <div>
                    R<sub>{index + 1}</sub>
                  </div>
                }
                type="number"
                variant="outlined"
                fullWidth
                value={value}
                onChange={(e) => handleChangeByIndex(index, +e.target.value)}
                helpButtonText={t(
                  "pages.driveshaftTolerance.components.other.permissibleUnbalanceMassCalculation.mobile.components.inputs.helpButtonText",
                  { number: index + 1 }
                )}
              />
            ))}
          </Stack>
        </Box>
        <Typography variant="h6" sx={{ my: 2 }}>
          {t("units.mass.unitSelection")}
        </Typography>
        <UnitsRadioGroup
          value={unit}
          options={allowedMassUnits}
          onChange={handleUnitChange}
        />
        <Box sx={{ display: { sm: "block", md: "none" } }}>
          <Typography variant="h6" sx={{ my: 2 }}>
            {t(
              "pages.driveshaftTolerance.components.other.permissibleUnbalanceMassCalculation.mobile.components.outputs.title"
            )}
            , {t("units.length.mm")}
          </Typography>
          <Stack sx={{ gap: 2 }}>
            {disbalanceMassValue.slice(0, supports).map((value, index) => (
              <HelpOutput
                label={
                  <div>
                    m<sub>{index + 1}</sub>
                  </div>
                }
                helpButtonText={t(
                  "pages.driveshaftTolerance.components.other.permissibleUnbalanceMassCalculation.mobile.components.outputs.helpButtonText",
                  { number: index + 1 }
                )}
              >
                {value}
              </HelpOutput>
            ))}
          </Stack>
        </Box>
        <TableContainer
          component={Paper}
          sx={{
            p: 2,
            maxWidth: "calc(100% - 35px)",
            display: { xs: "none", sm: "none", md: "table" },
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ fontSize: "1.1rem" }}>
                <TableCell width="25%" sx={{ fontSize: "inherit" }}>
                  {t(
                    "pages.driveshaftTolerance.components.other.permissibleUnbalanceMassCalculation.table.header.label",
                    { number: supports }
                  )}
                </TableCell>
                {radii.slice(0, supports).map((_, index) => (
                  <TableCell
                    key={index}
                    sx={{ textAlign: "center", fontSize: "inherit" }}
                  >
                    {index + 1}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell component="th" width="25%">
                  <Typography variant="body1">
                    {t(
                      "pages.driveshaftTolerance.components.other.permissibleUnbalanceMassCalculation.table.inputRow.label"
                    )}
                    , {unitLabel}
                  </Typography>
                </TableCell>
                {radii.slice(0, supports).map((value, index) => (
                  <TableCell key={index} component="th">
                    <TextField
                      variant="standard"
                      type="number"
                      sx={{
                        "&": {
                          width: "100%",
                        },
                        "& .MuiInputBase-root": {
                          // border: "none",
                          borderColor: "rgba(0, 0, 0, 0.23)",
                          borderRadius: "4px",
                          borderWidth: "1px",
                          borderStyle: "solid",
                          width: "100%",
                          padding: "10px 0px",
                          "&:hover": {
                            "&:not(.Mui-disabled, .Mui-error):before": {
                              border: "none",
                            },
                          },
                          "&:after, &:before": {
                            border: "none",
                          },
                          "& .MuiInputBase-input": {
                            textAlign: "center",
                            width: "100%",
                          },
                          "& input[type='number']": {
                            "-moz-appearance": "textfield",
                          },
                          "& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button":
                            {
                              "-webkit-appearance": "none",
                              margin: 0,
                            },
                        },
                      }}
                      onChange={(e) =>
                        handleChangeByIndex(index, +e.target.value)
                      }
                      value={value}
                    />
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell width="30%">
                  <Typography variant="body1">
                    {t(
                      "pages.driveshaftTolerance.components.other.permissibleUnbalanceMassCalculation.table.outputRow.label"
                    )}
                    , {unitLabel}
                  </Typography>
                </TableCell>
                {disbalanceMassValue.map((value, index) => (
                  <TableCell
                    key={index}
                    sx={{ textAlign: "center", fontSize: "1rem" }}
                  >
                    {value}
                  </TableCell>
                ))}
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </AccordionDetails>
    </Accordion>
  );
}
